import React, { useState, useEffect, useRef, useCallback } from 'react';

// Feedback data with long text to showcase full-text visibility
const DATA = [
  { Name: 'Sarah Chen', Feedback: 'Dashboard loading time has improved significantly since the last update — pages that used to take 3-4 seconds now render almost instantly. Would love to see dark mode support added in the next release, especially for users who work late hours reviewing data.', Category: 'UX', Status: 'Open' },
  { Name: 'James Rivera', Feedback: 'The export to PDF feature works great for client reports. One suggestion: allow custom headers and footers in the exported documents so we can add our company branding and legal disclaimers without having to edit the PDF manually after export.', Category: 'Feature', Status: 'In Review' },
  { Name: 'Priya Patel', Feedback: 'Search across all columns is incredibly fast now. Previously it took several seconds with large datasets of 10k+ rows, but now it feels instant even on our biggest spreadsheets. This has been a game-changer for our research team during quarterly reviews.', Category: 'Performance', Status: 'Closed' },
  { Name: 'Marcus Johnson', Feedback: 'Mobile layout needs work — the table columns overlap on smaller screens and some text gets completely hidden behind other columns. Consider a card-based layout for mobile users, or at minimum allow horizontal scrolling with a fixed first column.', Category: 'Bug', Status: 'Open' },
  { Name: 'Emily Watson', Feedback: 'Love the new filtering options. Being able to save filter presets has saved our team hours of repetitive setup each week. We have 12 different views saved now and switching between them during client calls makes us look incredibly organized.', Category: 'UX', Status: 'Closed' },
  { Name: 'David Kim', Feedback: 'Integration with our CRM was seamless. The API documentation is clear, the webhook support works exactly as described, and we had everything connected within a single afternoon. Our sales team now sees live spreadsheet data directly in their pipeline view.', Category: 'Feature', Status: 'In Review' },
];

const ALL_STATUSES = ['Open', 'In Review', 'Closed'];

const COLUMNS = ['Name', 'Feedback', 'Category', 'Status'];
const MOBILE_COLUMNS = ['Name', 'Feedback', 'Status'];

const COL_WIDTHS = { Name: '140px', Feedback: '1fr', Category: '100px', Status: '100px' };
const MOBILE_COL_WIDTHS = { Name: '120px', Feedback: '1fr', Status: '90px' };

const HEADER_THEMES = {
  copper: 'bg-copper-500',
  navy: 'bg-navy-900',
  emerald: 'bg-emerald-700',
};

const VIEW_CONFIGS = [
  { name: 'All Feedback', columns: ['Name', 'Feedback', 'Category', 'Status'], theme: 'copper' },
  { name: 'Open Issues', columns: ['Name', 'Feedback', 'Status'], theme: 'navy' },
  { name: 'Executive Summary', columns: ['Name', 'Category', 'Status'], theme: 'emerald' },
];

const CAPTIONS = [
  'See your full data — no cut-offs, ever',
  'Detailed view for long-form content',
  'Add comments for team review',
  'Filter any column with multi-select',
  'Presentation-ready in one click',
  'Create and switch between custom views',
];

const STATUS_COLORS = {
  Open: 'bg-amber-100 text-amber-800',
  'In Review': 'bg-blue-100 text-blue-800',
  Closed: 'bg-emerald-100 text-emerald-700',
};

function HeroDemo() {
  const [step, setStep] = useState(0);
  const [captionKey, setCaptionKey] = useState(0);
  const [headerTheme, setHeaderTheme] = useState('copper');
  const [selectedRow, setSelectedRow] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [showComment, setShowComment] = useState(false);
  const [activeView, setActiveView] = useState(0);
  const [showViewTabs, setShowViewTabs] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(null);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [filteredStatuses, setFilteredStatuses] = useState(null);
  const timeoutsRef = useRef([]);
  const pausedRef = useRef(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const baseColumns = visibleColumns || (isMobile ? MOBILE_COLUMNS : COLUMNS);
  const columns = isMobile ? baseColumns.filter(c => c !== 'Category') : baseColumns;
  const colWidths = isMobile ? MOBILE_COL_WIDTHS : COL_WIDTHS;
  const hasFr = columns.some(c => (colWidths[c] || '').includes('fr'));
  const gridCols = hasFr
    ? columns.map(c => colWidths[c] || '1fr').join(' ')
    : columns.map(() => '1fr').join(' ');

  const displayData = filteredStatuses
    ? DATA.filter(row => filteredStatuses.includes(row.Status))
    : DATA;

  const clearTimeouts = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const addTimeout = useCallback((fn, delay) => {
    const id = setTimeout(fn, delay);
    timeoutsRef.current.push(id);
    return id;
  }, []);

  const typeText = useCallback((text, onChar, onDone) => {
    let i = 0;
    const typeNext = () => {
      if (i < text.length) {
        onChar(text.slice(0, i + 1));
        i++;
        addTimeout(typeNext, 60 + Math.random() * 50);
      } else if (onDone) {
        addTimeout(onDone, 300);
      }
    };
    addTimeout(typeNext, 200);
  }, [addTimeout]);

  const resetState = useCallback(() => {
    setHeaderTheme('copper');
    setSelectedRow(null);
    setCommentText('');
    setShowComment(false);
    setActiveView(0);
    setShowViewTabs(false);
    setHighlightedRow(null);
    setVisibleColumns(null);
    setShowFilterDropdown(false);
    setFilteredStatuses(null);
  }, []);

  const runSequence = useCallback(() => {
    if (pausedRef.current) return;
    clearTimeouts();
    resetState();

    const goStep = (s) => {
      setStep(s);
      setCaptionKey(prev => prev + 1);
    };

    // Step 0: Full text visible (0-3.5s)
    goStep(0);
    addTimeout(() => setHighlightedRow(0), 1000);

    // Step 1: Detail panel slides in (3.5-8s)
    addTimeout(() => {
      if (pausedRef.current) return;
      goStep(1);
      setHighlightedRow(null);
      setSelectedRow(DATA[1]);
    }, 3500);

    // Step 2: Add comment (8-12.5s)
    addTimeout(() => {
      if (pausedRef.current) return;
      goStep(2);
      setShowComment(true);
      addTimeout(() => {
        typeText('Looks good — let\'s prioritize this for Q2.', setCommentText);
      }, 500);
    }, 8000);

    // Step 3: Filter by status (12.5-17s)
    addTimeout(() => {
      if (pausedRef.current) return;
      goStep(3);
      setSelectedRow(null);
      setShowComment(false);
      setCommentText('');
      setShowFilterDropdown(true);
      // Uncheck "Closed" after a beat
      addTimeout(() => setFilteredStatuses(['Open', 'In Review']), 1200);
      // Then uncheck "In Review" too — show only Open
      addTimeout(() => setFilteredStatuses(['Open']), 2500);
    }, 12500);

    // Step 4: Presentation ready — theme change (17-20.5s)
    addTimeout(() => {
      if (pausedRef.current) return;
      goStep(4);
      setShowFilterDropdown(false);
      setFilteredStatuses(null);
      addTimeout(() => setHeaderTheme('navy'), 600);
      addTimeout(() => setHeaderTheme('emerald'), 2000);
    }, 17000);

    // Step 5: Multiple views (20.5-25s)
    addTimeout(() => {
      if (pausedRef.current) return;
      goStep(5);
      setShowViewTabs(true);
      const applyView = (i) => {
        const v = VIEW_CONFIGS[i];
        setActiveView(i);
        setHeaderTheme(v.theme);
        setVisibleColumns(v.columns);
      };
      applyView(0);
      addTimeout(() => applyView(1), 1500);
      addTimeout(() => applyView(2), 3000);
    }, 20500);

    // Reset + loop (25-27s)
    addTimeout(() => {
      if (pausedRef.current) return;
      setShowViewTabs(false);
      resetState();
    }, 25000);

    addTimeout(() => {
      if (!pausedRef.current) runSequence();
    }, 27000);
  }, [clearTimeouts, resetState, typeText, addTimeout]);

  useEffect(() => {
    runSequence();

    const handleVisibility = () => {
      if (document.hidden) {
        pausedRef.current = true;
        clearTimeouts();
      } else {
        pausedRef.current = false;
        resetState();
        runSequence();
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      clearTimeouts();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [runSequence, clearTimeouts, resetState]);

  const headerBg = HEADER_THEMES[headerTheme] || HEADER_THEMES.copper;

  return (
    <div className="max-w-5xl mx-auto text-left">
      {/* Caption pill */}
      <div className="mb-4 text-center">
        <span
          key={captionKey}
          className="inline-block px-5 py-2 bg-cream-50 border border-cream-300 rounded-full text-sm text-navy-900 font-medium hero-fade-in"
        >
          {CAPTIONS[step]}
        </span>
      </div>

      {/* Demo container */}
      <div className="bg-cream-50 border border-cream-300 rounded-xl shadow-lg overflow-hidden">
        {/* View tabs bar */}
        <div
          className="overflow-hidden transition-all duration-500 border-b border-cream-300 bg-cream-100"
          style={{ maxHeight: showViewTabs ? '48px' : '0px', borderBottomWidth: showViewTabs ? '1px' : '0px' }}
        >
          <div className="flex items-center gap-1 px-4 py-2">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-2">Views</span>
            {VIEW_CONFIGS.map((view, i) => (
              <button
                key={view.name}
                className={`px-3 py-1.5 rounded-md text-sm transition-all duration-300 ${
                  activeView === i
                    ? 'bg-copper-500 text-white font-medium'
                    : 'text-gray-600 hover:bg-cream-200'
                }`}
              >
                {view.name}
              </button>
            ))}
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-cream-300 bg-cream-100">
          <div className="relative flex-1 max-w-xs">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <div className="w-full pl-9 pr-3 py-2 border border-cream-300 rounded-lg bg-white text-sm text-gray-400 min-h-[36px]">
              Search all columns...
            </div>
          </div>

          {/* Filter button + dropdown */}
          <div className="relative">
            <button className={`px-3 py-2 border rounded-lg bg-white text-sm flex items-center gap-1.5 transition-colors ${
              showFilterDropdown ? 'border-copper-500 text-copper-600' : 'border-cream-300 text-gray-600'
            }`}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="hidden sm:inline">Filter</span>
              {filteredStatuses && (
                <span className="w-4 h-4 rounded-full bg-copper-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {filteredStatuses.length}
                </span>
              )}
            </button>

            {showFilterDropdown && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-white border border-cream-300 rounded-lg shadow-lg z-20 p-2 hero-fade-in">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 py-1">Status</p>
                {ALL_STATUSES.map(status => {
                  const checked = !filteredStatuses || filteredStatuses.includes(status);
                  return (
                    <label key={status} className="flex items-center gap-2 px-2 py-1.5 text-sm text-navy-900 rounded hover:bg-cream-100">
                      <span className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                        checked ? 'bg-copper-500 border-copper-500' : 'border-cream-300 bg-white'
                      }`}>
                        {checked && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </span>
                      <span className={`inline-block px-1.5 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status]}`}>
                        {status}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Theme dots (visible during step 4) */}
          {step === 4 && (
            <div className="hidden sm:flex items-center gap-1.5 hero-fade-in">
              {Object.entries(HEADER_THEMES).map(([name, cls]) => (
                <span
                  key={name}
                  className={`w-5 h-5 rounded-full ${cls} ${
                    headerTheme === name ? 'ring-2 ring-offset-1 ring-navy-900' : ''
                  } transition-all`}
                />
              ))}
            </div>
          )}

          <span className="text-xs text-gray-400 hidden sm:inline">product_feedback.csv</span>
        </div>

        <div className="relative">
          {/* Table area */}
          <div className="overflow-x-auto">
            <div className="min-w-[400px]">
              {/* Header row */}
              <div
                className={`grid transition-colors duration-500 ${headerBg}`}
                style={{ gridTemplateColumns: gridCols }}
              >
                {columns.map(col => (
                  <div
                    key={col}
                    className="px-4 py-2.5 text-left text-xs font-semibold text-white uppercase tracking-wide"
                  >
                    {col}
                  </div>
                ))}
              </div>

              {/* Data rows */}
              <div>
                {displayData.map((row, i) => (
                  <div
                    key={row.Name}
                    className={`grid items-start border-b border-cream-200 transition-all duration-300 cursor-pointer ${
                      highlightedRow === i ? 'bg-copper-500/10' : ''
                    } ${selectedRow === row ? 'bg-cream-200' : 'hover:bg-cream-100'}`}
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    <div className={`px-4 py-3 text-sm text-navy-900 font-medium transition-all`}>{row.Name}</div>
                    {columns.includes('Feedback') && (
                      <div className={`px-4 py-3 text-sm text-gray-700 leading-relaxed transition-all`}>
                        {row.Feedback}
                      </div>
                    )}
                    {columns.includes('Category') && (
                      <div className={`px-4 py-3 text-sm text-gray-600 transition-all`}>{row.Category}</div>
                    )}
                    <div className="px-4 py-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[row.Status]}`}>
                        {row.Status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Detail panel — overlays table from the right */}
          {selectedRow && (
            <div className="hidden sm:block absolute right-0 top-0 bottom-0 w-72 bg-cream-50 border-l border-cream-300 shadow-lg hero-fade-in">
              {/* Panel header */}
              <div className="p-4 flex items-center justify-between border-b border-cream-300">
                <h3 className="text-sm font-semibold text-navy-900">Row Details</h3>
                <span className="p-1 text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </span>
              </div>

              {/* Panel content */}
              <div className="p-4 space-y-4">
                {COLUMNS.map(col => (
                  <div key={col}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                      {col}
                    </label>
                    <div className="text-sm text-navy-900 leading-relaxed">
                      {col === 'Status' ? (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[selectedRow[col]]}`}>
                          {selectedRow[col]}
                        </span>
                      ) : (
                        selectedRow[col]
                      )}
                    </div>
                  </div>
                ))}

                {/* Comment section */}
                {showComment && (
                  <div className="hero-fade-in border-t border-cream-300 pt-4">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Comments
                    </label>
                    <div className="bg-white border border-cream-300 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 rounded-full bg-copper-500 flex items-center justify-center">
                          <span className="text-white text-[10px] font-bold">Y</span>
                        </div>
                        <span className="text-xs text-gray-500">You — just now</span>
                      </div>
                      <div className="text-sm text-navy-900 min-h-[20px]">
                        {commentText}
                        {step === 2 && <span className="inline-block w-[2px] h-3.5 bg-navy-900 align-middle ml-[1px] hero-blink" />}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Pagination bar */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-cream-300 bg-cream-100 text-sm text-gray-500">
          <span>Showing {displayData.length} of {DATA.length} rows</span>
          <div className="flex items-center gap-1">
            <span className="px-2.5 py-1 rounded bg-copper-500 text-white text-xs font-medium">1</span>
            {displayData.length >= 6 && (
              <>
                <span className="px-2.5 py-1 rounded text-gray-500 text-xs">2</span>
                <span className="px-2.5 py-1 rounded text-gray-500 text-xs">3</span>
                <span className="px-2.5 py-1 rounded text-gray-500 text-xs">4</span>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes hero-fade-in {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .hero-fade-in {
          animation: hero-fade-in 0.35s ease-out;
        }
        @keyframes hero-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        .hero-blink {
          animation: hero-blink 0.8s step-end infinite;
        }
      `}</style>
    </div>
  );
}

export default HeroDemo;
